// v12
// Path: frontend/src/components/SyncHub/FieldMappingDesigner.jsx
// Inputs: mode ('pipeline' | 'standard'), inline (bool), token, config, standardMapping, sourceSystem, targetSystem, onSave, onClose, onResetDefaults, onSaved, onPipelineUpdated, setContext, systemContext, entityContext, notification, onClearNotification
// Outputs: Single-set workspace standard field mapping catalog and unified interactive ETL designer
// Summary: Full-featured mapping designer supporting top-header single-set workspace, strict 5-column Tree Grid alignment with inline Ghost Column actions, Tier 2 pipeline column header, hover-expandable AG Grid search filters, floating Toast notifications, persistent Tier 2 ghost rows, and stacking sticky headers.

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import MappingDesignerTable from './shared/MappingDesignerTable';
import DiffAnalyzerView from './shared/DiffAnalyzerView';
import SyncRunnerView from './shared/SyncRunnerView';
import SearchableSelect from './SearchableSelect';
import PresetAccordionItem from './shared/PresetAccordionItem';
import SetAccordionItem from './shared/SetAccordionItem';
import {
    NewSetModal,
    RenameSetModal,
    NewPipelineModal,
    RenamePipelineModal
} from './shared/StandardSetModals';
import { QUICKBOOKS_TARGET_ENTITIES, getTargetEntities, getQbEntityMeta, normalizeEntityName } from './entityCatalogs';
import { useSyncStore } from './useSyncStore';

// Auto-injected unbox helper for standardized API responses
const unbox = async (res) => {
    const payload = await res.json();
    if (payload && typeof payload === 'object' && payload.success === true && payload.data !== undefined) {
        return payload.data;
    }
    return payload;
};

// Helper to merge target fields with existing mapping assignments (for pipeline mode)
const mergeTargetFieldsWithMappings = (tFields, cFields, rawMappings) => {
    const allTargets = [...(tFields || [])];
    (cFields || []).forEach(cf => {
        if (!allTargets.some(t => t.name.toLowerCase() === cf.name.toLowerCase())) {
            allTargets.push(cf);
        }
    });

    const existingMap = new Map();
    (rawMappings || []).forEach(m => {
        if (m && m.target) {
            existingMap.set(m.target.toLowerCase(), {
                target: m.target,
                label: m.label || m.target,
                sources: Array.isArray(m.sources) && m.sources.length > 0
                    ? m.sources
                    : (m.source ? (m.source.includes('+') ? m.source.split('+').map(s => s.trim()) : [m.source]) : []),
                source: m.source || (Array.isArray(m.sources) ? m.sources.join(m.delimiter !== undefined ? m.delimiter : ' - ') : ''),
                delimiter: m.delimiter !== undefined ? m.delimiter : ' - ',
                template: m.template || ''
            });
        }
    });

    const result = [];
    for (const tf of allTargets) {
        const lower = tf.name.toLowerCase();
        if (existingMap.has(lower)) {
            result.push(existingMap.get(lower));
            existingMap.delete(lower);
        } else if (tf.required) {
            // Auto-spawn required destination field
            result.push({
                target: tf.name,
                label: tf.label || tf.name,
                sources: [],
                source: '',
                delimiter: ' - ',
                template: '',
                isRequired: true
            });
        }
    }

    for (const remaining of existingMap.values()) {
        result.push(remaining);
    }

    return result;
};

export const FieldMappingDesigner = ({
    mode = 'pipeline', // 'pipeline' or 'standard'
    inline = false,
    token,
    config = {},
    standardMapping = {},
    sourceSystem: explicitSourceSystem,
    targetSystem: explicitTargetSystem,
    onSave,
    onSaved,
    onClose,
    onResetDefaults,
    onPipelineUpdated,
    setContext,
    systemContext,
    entityContext,
    notification,
    onClearNotification
}) => {
    const isStandardMode = mode === 'standard';

    // ══════════════════════════════════════════════════════════════════════════
    // STANDARD MODE STATE (SINGLE-SET TOP-HEADER WORKSPACE)
    // ══════════════════════════════════════════════════════════════════════════
    const [standardSets, setStandardSets] = useState([]);
    const [standardMappings, setStandardMappings] = useState([]);
    const [selectedSetName, setSelectedSetName] = useState(setContext || '');
    
    // Strict Single-Expansion State for Pipelines (None open by default)
    const [expandedPresetId, setExpandedPresetId] = useState(null);
    const [isAutoSaving, setIsAutoSaving] = useState(false);

    // Pipeline Filter inside selected set (AG Grid expandable ghost search)
    const [pipelineFilter, setPipelineFilter] = useState('');
    const [isPipelineSearchExpanded, setIsPipelineSearchExpanded] = useState(false);

    // Tier 2 Pipeline Ghost Row State
    const [ghostPipelineName, setGhostPipelineName] = useState('');
    const [ghostPipelineNameUserEdited, setGhostPipelineNameUserEdited] = useState(false);
    const [ghostSourceTable, setGhostSourceTable] = useState('');

    const [stdSourceSystem, setStdSourceSystem] = useState(explicitSourceSystem || 'Passport');
    const [stdTargetSystem, setStdTargetSystem] = useState(explicitTargetSystem || 'QuickBooks');
    const [stdAvailableSources, setStdAvailableSources] = useState(['Passport', 'QuickBooks', 'Xero']);
    const [stdAvailableTargets, setStdAvailableTargets] = useState(['QuickBooks', 'Xero', 'Passport']);
    const [loadingStandard, setLoadingStandard] = useState(false);

    // Modal dialogs for Standard Sets & Pipelines
    const [showNewSetModal, setShowNewSetModal] = useState(false);
    const [showRenameSetModal, setShowRenameSetModal] = useState(false);
    const [renamingSetName, setRenamingSetName] = useState(null);
    const [showNewPipelineModal, setShowNewPipelineModal] = useState(false);
    const [addingToSetName, setAddingToSetName] = useState(null);
    const [showRenamePipelineModal, setShowRenamePipelineModal] = useState(false);
    const [renamingPreset, setRenamingPreset] = useState(null);

    // Tier 1 Set Actions Menu state & click outside dismissal
    const [showSetActionsMenu, setShowSetActionsMenu] = useState(false);
    const setActionsMenuRef = useRef(null);

    useEffect(() => {
        if (!showSetActionsMenu) return;
        const handleOutside = (e) => {
            if (setActionsMenuRef.current && !setActionsMenuRef.current.contains(e.target)) {
                setShowSetActionsMenu(false);
            }
        };
        document.addEventListener('mousedown', handleOutside);
        return () => document.removeEventListener('mousedown', handleOutside);
    }, [showSetActionsMenu]);

    // ══════════════════════════════════════════════════════════════════════════
    // PIPELINE MODE STATE
    // ══════════════════════════════════════════════════════════════════════════
    const effectiveSourceSystem = (
        explicitSourceSystem ||
        (isStandardMode ? stdSourceSystem : config.SourceSystem) ||
        'Passport'
    ).trim();
    const isPassportSource = effectiveSourceSystem.toLowerCase() === 'passport';

    const [pipelineTab, setPipelineTab] = useState('mappings');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [saveMessage, setSaveMessage] = useState(null);
    const [definitions, setDefinitions] = useState(null);
    const [mappings, setMappings] = useState([]);
    const [searchFilter, setSearchFilter] = useState('');
    const [isDirty, setIsDirty] = useState(false);

    // Auto-dismiss saveMessage and notification after 3.5s
    useEffect(() => {
        if (saveMessage) {
            const timer = setTimeout(() => {
                setSaveMessage(null);
            }, 3500);
            return () => clearTimeout(timer);
        }
    }, [saveMessage]);

    useEffect(() => {
        if (notification) {
            const timer = setTimeout(() => {
                if (onClearNotification) onClearNotification();
            }, 3500);
            return () => clearTimeout(timer);
        }
    }, [notification, onClearNotification]);

    // Light dismiss: Close on Escape key
    const handleClose = useCallback(async () => {
        if (isDirty && !(await useSyncStore.getState().showConfirm('You have unsaved changes. Are you sure you want to close?'))) {
            return;
        }
        if (onClose) onClose();
    }, [isDirty, onClose]);

    useEffect(() => {
        if (inline || !onClose) return;
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                e.stopPropagation();
                handleClose();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [inline, onClose, handleClose]);

    // Active Entity and Target QuickBooks Entity for pipeline mode
    const initialEntity = normalizeEntityName(config.TargetQBEntity || config.EntityType || 'Customer');
    const [selectedEntity, setSelectedEntity] = useState(initialEntity);
    const [selectedQBEntity, setSelectedQBEntity] = useState(initialEntity);

    const meta = getQbEntityMeta(selectedQBEntity);
    const initialTable = config.TableName || meta?.defaultTable || '';
    const [selectedTable, setSelectedTable] = useState(initialTable);

    // Table & Schema catalogs
    const [availableTables, setAvailableTables] = useState([]);
    const [loadingTables, setLoadingTables] = useState(false);
    const [qbCustomFields, setQbCustomFields] = useState([]);

    // Live Samples for Pipeline Mode
    const [sampleRecords, setSampleRecords] = useState([]);
    const [activeSampleIndex, setActiveSampleIndex] = useState(0);
    const [qbSampleRecords, setQbSampleRecords] = useState([]);
    const [activeQbSampleIndex, setActiveQbSampleIndex] = useState(0);

    // Inspect & Diff State for Pipeline Mode
    const [diffLimit, setDiffLimit] = useState(50);
    const [loadingDiff, setLoadingDiff] = useState(false);
    const [diffPreviewData, setDiffPreviewData] = useState(null);
    const [diffError, setDiffError] = useState(null);
    const [diffDetailTab, setDiffDetailTab] = useState('changes');

    // Sync Execution State for Pipeline Mode
    const [syncLimit, setSyncLimit] = useState(50);
    const [loadingSync, setLoadingSync] = useState(false);
    const [syncResult, setSyncResult] = useState(null);

    // ══════════════════════════════════════════════════════════════════════════
    // DATA FETCHING: AVAILABLE TABLES (Passport ERP + Live DB)
    // ══════════════════════════════════════════════════════════════════════════
    useEffect(() => {
        let isMounted = true;
        const fetchAvailableTables = async () => {
            setLoadingTables(true);
            const connPrefix = config.SourceConnPrefix || standardMapping.SourceConnPrefix;
            const dbName = config.SourceDatabaseName || standardMapping.SourceDatabaseName || 'pbsdata00';

            try {
                let passportTables = [];
                if (isPassportSource || isStandardMode) {
                    try {
                        const dictRes = await fetch('/api/sync/data-dictionary/tables', {
                            headers: { 'Authorization': `Bearer ${token}` }
                        });
                        if (dictRes.ok) {
                            const data = await unbox(dictRes);
                            if (Array.isArray(data)) {
                                passportTables = data.map(t => ({
                                    name: t.TableName,
                                    description: t.Description || (t.Module ? `${t.Module} Table` : 'Passport Table'),
                                    module: t.Module,
                                    fieldCount: t.FieldCount,
                                    isCanonical: true
                                }));
                            }
                        }
                    } catch (e) {
                        console.warn('[FieldMappingDesigner] Error fetching Passport.Tables:', e);
                    }
                }

                let liveDbTables = [];
                if (connPrefix && dbName) {
                    try {
                        const params = new URLSearchParams({ connPrefix, databaseName: dbName });
                        const res = await fetch(`/api/sync/database-tables?${params.toString()}`, {
                            headers: { 'Authorization': `Bearer ${token}` }
                        });
                        if (res.ok) {
                            const data = await unbox(res);
                            const rawList = Array.isArray(data) ? data : (Array.isArray(data?.tables) ? data.tables : []);
                            liveDbTables = rawList.map(t => {
                                const tName = typeof t === 'string' ? t : (t.name || t.TABLE_NAME || '');
                                return {
                                    name: tName,
                                    description: (typeof t === 'object' && t.description) ? t.description : undefined,
                                    module: (typeof t === 'object' && t.module) ? t.module : undefined,
                                    isLive: true
                                };
                            }).filter(t => t.name);
                        }
                    } catch (err) {
                        console.warn('[FieldMappingDesigner] Could not fetch live database tables:', err);
                    }
                }

                if (!isMounted) return;

                if (isPassportSource || passportTables.length > 0) {
                    const passportMap = new Map();
                    passportTables.forEach(t => passportMap.set(t.name.toUpperCase(), t));

                    const combined = [];
                    const seenNames = new Set();

                    liveDbTables.forEach(lt => {
                        const upper = lt.name.toUpperCase();
                        if (seenNames.has(upper)) return;
                        seenNames.add(upper);
                        const baseName = upper.replace(/\d+$/, '');
                        const matched = passportMap.get(baseName) || passportMap.get(upper);
                        combined.push({
                            name: lt.name,
                            description: matched ? (matched.description || matched.name) : 'Live Database Table',
                            module: matched ? matched.module : undefined,
                            isLive: true
                        });
                    });

                    passportTables.forEach(pt => {
                        const upper = pt.name.toUpperCase();
                        if (!seenNames.has(upper)) {
                            seenNames.add(upper);
                            combined.push(pt);
                        }
                    });

                    setAvailableTables(combined);
                } else {
                    setAvailableTables(liveDbTables);
                }
            } catch (err) {
                console.error('[FieldMappingDesigner] Error assembling available tables:', err);
            } finally {
                if (isMounted) setLoadingTables(false);
            }
        };

        fetchAvailableTables();
        return () => { isMounted = false; };
    }, [token, config.SourceConnPrefix, config.SourceDatabaseName, standardMapping.SourceConnPrefix, standardMapping.SourceDatabaseName, isStandardMode, isPassportSource]);

    // ══════════════════════════════════════════════════════════════════════════
    // STANDARD MODE: FETCH ALL SETS & PRESETS
    // ══════════════════════════════════════════════════════════════════════════
    const fetchStandardData = useCallback(async () => {
        if (!isStandardMode) return;
        setLoadingStandard(true);
        try {
            const params = new URLSearchParams({
                sourceSystem: stdSourceSystem,
                targetSystem: stdTargetSystem,
                setName: 'ALL'
            });
            const res = await fetch(`/api/sync/standard-mappings?${params.toString()}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await unbox(res);
                const fetchedMappings = Array.isArray(data.mappings) ? data.mappings : [];
                setStandardMappings(fetchedMappings);

                if (Array.isArray(data.setsMeta) && data.setsMeta.length > 0) {
                    setStandardSets(data.setsMeta);
                    setSelectedSetName(prev => {
                        if (prev && data.setsMeta.some(s => (s.setName || s.name || s) === prev)) return prev;
                        const def = data.setsMeta.find(s => s.isDefault)?.setName || data.setsMeta[0]?.setName;
                        return def || 'Default';
                    });
                } else if (Array.isArray(data.availableSets)) {
                    setStandardSets(data.availableSets.map((s, idx) => ({ setName: s, isDefault: idx === 0 })));
                    setSelectedSetName(prev => {
                        if (prev && data.availableSets.includes(prev)) return prev;
                        return data.defaultSetName || data.availableSets[0] || 'Default';
                    });
                }

                if (Array.isArray(data.availableSourceSystems)) setStdAvailableSources(data.availableSourceSystems);
                if (Array.isArray(data.availableTargetSystems)) setStdAvailableTargets(data.availableTargetSystems);
            }
        } catch (err) {
            console.error('[FieldMappingDesigner] Error fetching standard data:', err);
        } finally {
            setLoadingStandard(false);
        }
    }, [isStandardMode, stdSourceSystem, stdTargetSystem, token]);

    useEffect(() => {
        fetchStandardData();
    }, [fetchStandardData]);

    // Group standard mapping presets by SetName
    const presetsBySet = useMemo(() => {
        const map = {};
        standardMappings.forEach(m => {
            const sName = m.SetName || 'Default';
            if (!map[sName]) map[sName] = [];
            map[sName].push(m);
        });
        return map;
    }, [standardMappings]);

    // Derived target entities catalog for standard mode
    const stdTargetEntities = useMemo(() => {
        return getTargetEntities(stdTargetSystem);
    }, [stdTargetSystem]);

    // Current active Set metadata
    const currentSet = useMemo(() => {
        return standardSets.find(s => (s.setName || s.name || s) === selectedSetName) || { setName: selectedSetName || 'Default', isDefault: false };
    }, [standardSets, selectedSetName]);

    const isCurrentSetDefault = !!currentSet.isDefault;

    // Presets in currently selected Set
    const currentPresets = useMemo(() => {
        return presetsBySet[selectedSetName] || [];
    }, [presetsBySet, selectedSetName]);

    // Filter presets based on user search input
    const filteredPresets = useMemo(() => {
        if (!pipelineFilter.trim()) return currentPresets;
        const q = pipelineFilter.toLowerCase().trim();
        return currentPresets.filter(p => {
            const name = (p.MappingName || '').toLowerCase();
            const entity = (p.TargetQBEntity || p.EntityType || '').toLowerCase();
            const table = (p.SourceTable || '').toLowerCase();
            return name.includes(q) || entity.includes(q) || table.includes(q);
        });
    }, [currentPresets, pipelineFilter]);

    // Summary calculations across presets in currently selected Set
    const setStats = useMemo(() => {
        const count = currentPresets.length;
        let mappedCount = 0;
        currentPresets.forEach(p => {
            const raw = p.mappings || (p.FieldMappingsJson ? JSON.parse(p.FieldMappingsJson || '[]') : []);
            if (Array.isArray(raw)) {
                mappedCount += raw.filter(m => (m.sources && m.sources.length > 0) || (m.source && m.source.trim())).length;
            }
        });
        return { pipelineCount: count, mappedFieldsCount: mappedCount };
    }, [currentPresets]);

    // Set of all target entity names already assigned to pipelines in this Set
    const assignedEntitiesInSet = useMemo(() => {
        const setMap = new Set();
        currentPresets.forEach(p => {
            const ent = normalizeEntityName(p.TargetQBEntity || p.EntityType || '');
            if (ent) setMap.add(ent.toLowerCase());
        });
        return setMap;
    }, [currentPresets]);

    // Available target entities for Tier 2 Pipeline Ghost Row strictly excluding assigned entities in this Set
    const ghostEntityOptions = useMemo(() => {
        return stdTargetEntities
            .filter(ent => {
                const eName = typeof ent === 'string' ? ent : (ent.entity || ent.name);
                return !assignedEntitiesInSet.has(eName.toLowerCase());
            })
            .map(ent => {
                const eName = typeof ent === 'string' ? ent : (ent.entity || ent.name);
                const eLabel = typeof ent === 'string' ? ent : (ent.label || ent.name || ent.entity);
                return { value: eName, label: eLabel };
            });
    }, [stdTargetEntities, assignedEntitiesInSet]);

    // Table options for Ghost Row
    const ghostTableOptions = useMemo(() => {
        return availableTables.map(t => {
            const tName = typeof t === 'string' ? t : (t.name || t.TableName || '');
            const fCount = t.fieldCount || t.FieldCount || (t.columns ? t.columns.length : 0);
            return {
                value: tName,
                label: fCount > 0 ? `${tName} (${fCount} cols)` : tName
            };
        });
    }, [availableTables]);

    // ── Standard Set Action Handlers ──────────────────────────────────────────
    const handleMakeDefaultSet = async (setName) => {
        const targetSet = setName || selectedSetName;
        try {
            const res = await fetch('/api/sync/standard-sets/set-default', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ setName: targetSet, sourceSystem: stdSourceSystem, targetSystem: stdTargetSystem })
            });
            if (!res.ok) {
                const err = await unbox(res);
                throw new Error(err.error || 'Failed to designate default set');
            }
            setSaveMessage({ type: 'success', text: `Standard set "${targetSet}" designated as default.` });
            await fetchStandardData();
        } catch (err) {
            useSyncStore.getState().showAlert(err.message);
        }
    };

    const handleDuplicateSet = async (setName) => {
        const targetSet = setName || selectedSetName;
        const copyName = `${targetSet} (Copy)`;
        try {
            const res = await fetch('/api/sync/standard-sets', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({
                    newSetName: copyName,
                    cloneFromSetName: targetSet,
                    sourceSystem: stdSourceSystem,
                    targetSystem: stdTargetSystem
                })
            });
            if (!res.ok) {
                const err = await unbox(res);
                throw new Error(err.error || 'Failed to duplicate set');
            }
            setSaveMessage({ type: 'success', text: `Standard set "${targetSet}" duplicated as "${copyName}".` });
            await fetchStandardData();
            setSelectedSetName(copyName);
            setExpandedPresetId(null);
        } catch (err) {
            useSyncStore.getState().showAlert(err.message);
        }
    };

    const handleCreateSetSubmit = async ({ newSetName, cloneFromSetName }) => {
        const res = await fetch('/api/sync/standard-sets', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({
                newSetName,
                cloneFromSetName,
                sourceSystem: stdSourceSystem,
                targetSystem: stdTargetSystem
            })
        });
        if (!res.ok) {
            const err = await unbox(res);
            throw new Error(err.error || 'Failed to create set');
        }
        setSaveMessage({ type: 'success', text: `Standard set "${newSetName}" created successfully.` });
        await fetchStandardData();
        setSelectedSetName(newSetName);
        setExpandedPresetId(null);
    };

    const handleRenameSetSubmit = async (newSetName) => {
        if (!renamingSetName) return;
        const res = await fetch(`/api/sync/standard-sets/${encodeURIComponent(renamingSetName)}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({ newSetName })
        });
        if (!res.ok) {
            const err = await unbox(res);
            throw new Error(err.error || 'Failed to rename set');
        }
        setSaveMessage({ type: 'success', text: `Set renamed to "${newSetName}".` });
        await fetchStandardData();
        setSelectedSetName(newSetName);
    };

    const handleDeleteSet = async (setName) => {
        const targetSet = setName || selectedSetName;
        const confirm = await useSyncStore.getState().showConfirm(`Are you sure you want to delete Standard Set "${targetSet}"? All pipelines inside this set will be removed.`);
        if (!confirm) return;
        try {
            const res = await fetch(`/api/sync/standard-sets/${encodeURIComponent(targetSet)}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!res.ok) {
                const err = await unbox(res);
                throw new Error(err.error || 'Failed to delete standard set');
            }
            setSaveMessage({ type: 'success', text: `Standard set "${targetSet}" deleted.` });
            await fetchStandardData();
            setSelectedSetName(prev => {
                const remaining = standardSets.filter(s => (s.setName || s.name || s) !== targetSet);
                const next = remaining.find(s => s.isDefault)?.setName || remaining[0]?.setName || remaining[0]?.name || remaining[0] || 'Default';
                return next;
            });
            setExpandedPresetId(null);
        } catch (err) {
            useSyncStore.getState().showAlert(err.message);
        }
    };

    const handleResetSetDefaults = async (setName) => {
        const targetSet = setName || selectedSetName;
        const confirm = await useSyncStore.getState().showConfirm(`Reset all pipelines in "${targetSet}" to standard defaults?`);
        if (!confirm) return;
        try {
            await fetchStandardData();
            setSaveMessage({ type: 'success', text: `Standard set "${targetSet}" reset to defaults.` });
        } catch (err) {
            useSyncStore.getState().showAlert(err.message);
        }
    };

    // ── Standard Preset / Pipeline Handlers ───────────────────────────────────
    const handleAddNewPipelineSubmit = async ({ setName, targetEntity, sourceTable, pipelineName }) => {
        const targetSetName = setName || addingToSetName || selectedSetName;
        if (!targetSetName) return;
        const res = await fetch('/api/sync/standard-mappings', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({
                setName: targetSetName,
                sourceSystem: stdSourceSystem,
                targetSystem: stdTargetSystem,
                entityType: targetEntity,
                targetQBEntity: targetEntity,
                sourceTable,
                mappingName: pipelineName,
                mappings: []
            })
        });
        if (!res.ok) {
            const err = await unbox(res);
            throw new Error(err.error || 'Failed to create pipeline');
        }
        let createdData = null;
        try {
            createdData = await unbox(res);
        } catch (_) {}
        const newPreset = {
            SetName: targetSetName,
            SourceSystem: stdSourceSystem,
            TargetSystem: stdTargetSystem,
            EntityType: targetEntity,
            TargetQBEntity: targetEntity,
            SourceTable: sourceTable,
            MappingName: pipelineName,
            mappings: [],
            FieldMappingsJson: '[]',
            ...(createdData && typeof createdData === 'object' ? createdData : {})
        };
        setStandardMappings(prev => [...prev, newPreset]);
        setSaveMessage({ type: 'success', text: `Pipeline "${pipelineName}" added to set "${targetSetName}".` });
        setSelectedSetName(targetSetName);
        setExpandedPresetId(targetEntity);
    };

    // Auto-fill logic when user selects Target Entity in Tier 2 Pipeline Ghost Row
    const handleGhostEntitySelect = (chosenEntity) => {
        if (!chosenEntity) return;
        const canonical = normalizeEntityName(chosenEntity);
        const meta = getQbEntityMeta(canonical);
        const autoTable = ghostSourceTable || meta?.defaultTable || 'CUSFIL';

        let effectiveName = ghostPipelineName;
        if (!ghostPipelineNameUserEdited || !ghostPipelineName.trim()) {
            effectiveName = `${selectedSetName} - ${stdSourceSystem} ${canonical} -> ${stdTargetSystem}`;
        }

        handleAddNewPipelineSubmit({
            setName: selectedSetName,
            targetEntity: canonical,
            sourceTable: autoTable,
            pipelineName: effectiveName
        });

        setGhostPipelineName('');
        setGhostPipelineNameUserEdited(false);
        setGhostSourceTable('');
    };

    const handleSavePreset = async (payload) => {
        const res = await fetch('/api/sync/standard-mappings', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify(payload)
        });
        if (!res.ok) {
            const err = await unbox(res);
            throw new Error(err.error || 'Failed to save preset mappings');
        }
        // Optimistic state update: Update local standardMappings array without calling fetchStandardData()!
        // This stops full-screen refreshes and row flashing on auto-save
        setStandardMappings(prev => prev.map(m => {
            const matchId = payload.mappingId && m.MappingID === payload.mappingId;
            const matchEntity = !payload.mappingId && (m.SetName || 'Default') === payload.setName && (m.TargetQBEntity || m.EntityType) === payload.entityType;
            if (matchId || matchEntity) {
                return {
                    ...m,
                    SourceTable: payload.sourceTable || m.SourceTable,
                    MappingName: payload.mappingName || m.MappingName,
                    mappings: payload.mappings || m.mappings,
                    FieldMappingsJson: JSON.stringify(payload.mappings || [])
                };
            }
            return m;
        }));
    };

    const handleDuplicatePreset = async (preset) => {
        const copyName = `${preset.MappingName} (Copy)`;
        try {
            const res = await fetch('/api/sync/standard-mappings', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({
                    setName: preset.SetName,
                    sourceSystem: preset.SourceSystem,
                    targetSystem: preset.TargetSystem,
                    entityType: preset.EntityType,
                    targetQBEntity: preset.TargetQBEntity,
                    sourceTable: preset.SourceTable,
                    mappingName: copyName,
                    description: preset.Description,
                    mappings: preset.mappings || []
                })
            });
            if (!res.ok) {
                const err = await unbox(res);
                throw new Error(err.error || 'Failed to duplicate preset');
            }
            setSaveMessage({ type: 'success', text: `Pipeline "${preset.MappingName}" duplicated.` });
            await fetchStandardData();
        } catch (err) {
            useSyncStore.getState().showAlert(err.message);
        }
    };

    const handleRenamePipelineSubmit = async (newName) => {
        if (!renamingPreset) return;
        const res = await fetch('/api/sync/standard-mappings', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({
                mappingId: renamingPreset.MappingID,
                setName: renamingPreset.SetName,
                sourceSystem: renamingPreset.SourceSystem,
                targetSystem: renamingPreset.TargetSystem,
                entityType: renamingPreset.EntityType,
                targetQBEntity: renamingPreset.TargetQBEntity,
                sourceTable: renamingPreset.SourceTable,
                mappingName: newName,
                description: renamingPreset.Description,
                mappings: renamingPreset.mappings || []
            })
        });
        if (!res.ok) {
            const err = await unbox(res);
            throw new Error(err.error || 'Failed to rename pipeline');
        }
        setSaveMessage({ type: 'success', text: `Pipeline renamed to "${newName}".` });
        await fetchStandardData();
    };

    const handleInlineRenamePipeline = async (updatedPreset) => {
        const newName = updatedPreset.MappingName;
        // Optimistic update in local standardMappings array without modal popups
        setStandardMappings(prev => prev.map(m => {
            const matchId = updatedPreset.MappingID && m.MappingID === updatedPreset.MappingID;
            const matchEntity = !updatedPreset.MappingID && (m.SetName || 'Default') === (updatedPreset.SetName || 'Default') && (m.TargetQBEntity || m.EntityType) === (updatedPreset.TargetQBEntity || updatedPreset.EntityType);
            if (matchId || matchEntity) {
                return { ...m, MappingName: newName };
            }
            return m;
        }));
        try {
            await handleSavePreset({
                mappingId: updatedPreset.MappingID,
                setName: updatedPreset.SetName || selectedSetName,
                sourceSystem: updatedPreset.SourceSystem || stdSourceSystem,
                targetSystem: updatedPreset.TargetSystem || stdTargetSystem,
                entityType: updatedPreset.EntityType || updatedPreset.TargetQBEntity,
                targetQBEntity: updatedPreset.TargetQBEntity || updatedPreset.EntityType,
                sourceTable: updatedPreset.SourceTable,
                mappingName: newName,
                mappings: updatedPreset.mappings || []
            });
        } catch (err) {
            console.error('Failed to save renamed pipeline in background:', err);
        }
    };

    const handleDeletePreset = async (preset) => {
        const confirm = await useSyncStore.getState().showConfirm(`Are you sure you want to delete pipeline "${preset.MappingName || preset.TargetQBEntity}"?`);
        if (!confirm) {
            return false; // Crucial: signals cancellation so caller resets isDeleting state
        }
        try {
            const targetId = preset.MappingID || 0;
            const queryParams = new URLSearchParams({
                setName: preset.SetName || selectedSetName,
                entityType: preset.TargetQBEntity || preset.EntityType || ''
            });
            const res = await fetch(`/api/sync/standard-mappings/${targetId}?${queryParams.toString()}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!res.ok) {
                const err = await unbox(res);
                throw new Error(err.error || 'Failed to delete preset');
            }
            // Optimistic removal without full fetchStandardData()
            setStandardMappings(prev => prev.filter(m => {
                if (preset.MappingID && m.MappingID) return m.MappingID !== preset.MappingID;
                return !((m.SetName || 'Default') === (preset.SetName || 'Default') && (m.TargetQBEntity || m.EntityType) === (preset.TargetQBEntity || preset.EntityType));
            }));
            setSaveMessage({ type: 'success', text: `Pipeline deleted.` });
            if (expandedPresetId === (preset.MappingID || preset.TargetQBEntity || preset.EntityType)) {
                setExpandedPresetId(null);
            }
            return true;
        } catch (err) {
            useSyncStore.getState().showAlert(err.message);
            throw err;
        }
    };

    const handleResetPresetDefaults = async (preset) => {
        const confirm = await useSyncStore.getState().showConfirm(`Reset mappings for "${preset.MappingName || preset.TargetQBEntity}" to default preset?`);
        if (!confirm) return;
        try {
            await fetchStandardData();
            setSaveMessage({ type: 'success', text: `Pipeline "${preset.MappingName}" reset.` });
        } catch (err) {
            useSyncStore.getState().showAlert(err.message);
        }
    };

    // ══════════════════════════════════════════════════════════════════════════
    // PIPELINE MODE: ACTIVE TABLE, DEFINITIONS, DIFF & SYNC
    // ══════════════════════════════════════════════════════════════════════════
    const activeTableName = useMemo(() => {
        if (selectedTable && selectedTable.trim()) return selectedTable.trim();
        if (config.TableName && config.TableName.trim()) return config.TableName.trim();
        return meta?.defaultTable || 'CUSFIL';
    }, [selectedTable, config.TableName, meta?.defaultTable]);

    const sourceConnPrefix = config.SourceConnPrefix || '';
    const sourceDatabaseName = config.SourceDatabaseName || 'pbsdata00';
    const rawMappingsStr = config.FieldMappingsJson || config.FieldMappings || '';

    // Fetch Field Definitions for Pipeline Mode
    const fetchDefinitions = useCallback(async () => {
        if (isStandardMode) return;
        setLoading(true);
        try {
            const params = new URLSearchParams({
                entityType: selectedEntity,
                sourceConnPrefix,
                sourceDatabaseName,
                tableName: activeTableName
            });

            const res = await fetch(`/api/sync/field-definitions/${encodeURIComponent(selectedEntity)}?${params.toString()}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!res.ok) {
                const errData = await unbox(res).catch(() => ({}));
                throw new Error(errData.error || `Failed to load field definitions (HTTP ${res.status})`);
            }
            const data = await unbox(res);
            setDefinitions(data);

            let initialRaw = [];
            if (rawMappingsStr) {
                try {
                    const parsed = typeof rawMappingsStr === 'string' ? JSON.parse(rawMappingsStr) : rawMappingsStr;
                    if (Array.isArray(parsed)) initialRaw = parsed;
                } catch (e) {}
            }

            const targetFields = data.targetFields || [];
            const merged = mergeTargetFieldsWithMappings(targetFields, qbCustomFields, initialRaw);
            setMappings(merged);
            setIsDirty(false);
        } catch (err) {
            console.error('[FieldMappingDesigner] Error fetching definitions:', err);
            setSaveMessage({ type: 'error', text: err.message });
        } finally {
            setLoading(false);
        }
    }, [isStandardMode, selectedEntity, sourceConnPrefix, sourceDatabaseName, activeTableName, token, rawMappingsStr, qbCustomFields]);

    useEffect(() => {
        fetchDefinitions();
    }, [fetchDefinitions]);

    // Save field mappings for Pipeline Mode
    const handleSavePipeline = async (e) => {
        if (e) e.preventDefault();
        setSaving(true);
        setSaveMessage(null);
        try {
            const cleanMappings = mappings
                .filter(m => m.target && ((Array.isArray(m.sources) && m.sources.length > 0) || (m.source && m.source.trim().length > 0) || (m.template && m.template.trim().length > 0)))
                .map(m => ({
                    target: m.target.trim(),
                    label: m.label ? m.label.trim() : undefined,
                    source: m.source ? m.source.trim() : (m.sources ? m.sources.join(m.delimiter || ' - ') : ''),
                    sources: Array.isArray(m.sources) && m.sources.length > 0 ? m.sources : [(m.source || '').trim()],
                    delimiter: m.delimiter !== undefined ? m.delimiter : ' - ',
                    template: m.template || undefined
                }));

            const payload = {
                configId: config.ConfigID,
                tableName: activeTableName,
                targetQBEntity: selectedQBEntity,
                fieldMappingsJson: JSON.stringify(cleanMappings)
            };
            const res = await fetch('/api/sync/configs/field-mappings', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify(payload)
            });
            if (!res.ok) {
                const err = await unbox(res);
                throw new Error(err.error || 'Failed to save field mappings');
            }
            setSaveMessage({ type: 'success', text: `Field mappings for ${config.ConfigName || activeTableName} saved!` });
            setIsDirty(false);
            if (onSaved) onSaved();
            if (onPipelineUpdated) onPipelineUpdated();
        } catch (err) {
            setSaveMessage({ type: 'error', text: err.message });
        } finally {
            setSaving(false);
        }
    };

    // Run Inspect & Diff Analysis for Pipeline Mode
    const handleRunDiff = async () => {
        setLoadingDiff(true);
        setDiffError(null);
        try {
            const payload = {
                configId: config.ConfigID,
                accountId: config.AccountID,
                sourceConnPrefix: config.SourceConnPrefix,
                sourceDatabaseName: config.SourceDatabaseName,
                tableName: activeTableName,
                targetQBEntity: selectedQBEntity,
                entityType: config.EntityType || selectedEntity,
                limit: diffLimit
            };
            const res = await fetch('/api/sync/sync/preview', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify(payload)
            });
            if (!res.ok) {
                const err = await unbox(res);
                throw new Error(err.error || 'Dry run diff analysis failed');
            }
            const data = await unbox(res);
            setDiffPreviewData(data);
        } catch (err) {
            setDiffError(err.message);
        } finally {
            setLoadingDiff(false);
        }
    };

    // Execute Live Batch Sync for Pipeline Mode
    const handleExecuteLiveSync = async (isDryRun = false) => {
        setLoadingSync(true);
        setSyncResult(null);
        try {
            const endpoint = isDryRun ? '/api/sync/sync/preview' : '/api/sync/sync/execute';
            const payload = {
                configId: config.ConfigID,
                accountId: config.AccountID,
                sourceConnPrefix: config.SourceConnPrefix,
                sourceDatabaseName: config.SourceDatabaseName,
                tableName: activeTableName,
                targetQBEntity: selectedQBEntity,
                entityType: config.EntityType || selectedEntity,
                limit: syncLimit
            };
            const res = await fetch(endpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify(payload)
            });
            if (!res.ok) {
                const err = await unbox(res);
                throw new Error(err.error || 'Sync execution failed');
            }
            const data = await unbox(res);
            setSyncResult(data);
            if (onSaved) onSaved();
            if (onPipelineUpdated) onPipelineUpdated();
        } catch (err) {
            useSyncStore.getState().showAlert(`Execution Error: ${err.message}`);
        } finally {
            setLoadingSync(false);
        }
    };

    // Source columns for pipeline mode
    const sourceColumns = useMemo(() => {
        if (!definitions?.sourceColumns) return [];
        return definitions.sourceColumns.map(col => {
            if (typeof col === 'string') return { name: col, type: 'VARCHAR' };
            return {
                name: col.name || col.COLUMN_NAME,
                type: col.type || col.DATA_TYPE || 'VARCHAR',
                description: col.description || col.COLUMN_COMMENT
            };
        });
    }, [definitions?.sourceColumns]);

    const handleAddMappingPipeline = (newMapping) => {
        setMappings(prev => [...prev, newMapping]);
        setIsDirty(true);
    };

    const handleCustomMappingChangePipeline = (targetField, fieldKey, value) => {
        setMappings(prev => prev.map(m => {
            if (m.target !== targetField) return m;
            const updated = { ...m, [fieldKey]: value };
            if (fieldKey === 'sources' && Array.isArray(value)) {
                updated.source = value.join(m.delimiter !== undefined ? m.delimiter : ' - ');
            } else if (fieldKey === 'delimiter' && Array.isArray(m.sources)) {
                updated.source = m.sources.join(value);
            }
            return updated;
        }));
        setIsDirty(true);
    };

    const handleRemoveCustomRowPipeline = (targetField) => {
        setMappings(prev => prev.filter(m => m.target !== targetField));
        setIsDirty(true);
    };

    const handleResetMappingRowPipeline = (targetField) => {
        setMappings(prev => prev.map(m => {
            if (m.target === targetField) {
                return { ...m, sources: [], source: '' };
            }
            return m;
        }));
        setIsDirty(true);
    };

    // Formatted table options for SearchableSelect
    const tableSelectOptions = useMemo(() => {
        const list = (availableTables || []).map(t => ({
            value: t.name,
            label: t.name,
            description: t.description || (t.module ? `Module: ${t.module}` : ''),
            sublabel: t.isLive ? '[Live DB]' : (t.module ? `[${t.module}]` : '[Passport]')
        }));
        const current = (selectedTable || activeTableName || '').trim().toUpperCase();
        if (current && !list.some(o => o.value.toUpperCase() === current)) {
            list.unshift({
                value: current,
                label: current,
                description: 'Active ERP Table',
                sublabel: '[Current]'
            });
        }
        return list;
    }, [availableTables, selectedTable, activeTableName]);

    const activeMappedCount = mappings.filter(m => (m.sources && m.sources.length > 0) || (m.source && m.source.trim())).length;

    return (
        <div className={`flex flex-col bg-slate-900 text-slate-200 ${inline ? 'w-full rounded-xl border border-slate-750 shadow-xl' : 'h-full'}`}>
            {isStandardMode ? (
                /* ── STANDARD MODE: SINGLE-SET WORKSPACE (TREE GRID) ── */
                <div className="flex-1 overflow-y-auto overflow-x-auto p-0 relative pb-[65vh]">
                    <div className="min-w-[800px] flex flex-col">
                        {/* ══════════════════════════════════════════════════════════════════ */}
                        {/* TIER 1: TOP STICKY HEADER (4-Column Grid, h-7 + h-9, sticky top-0 z-50) */}
                        {/* ══════════════════════════════════════════════════════════════════ */}
                        {/* ══════════════════════════════════════════════════════════════════ */}
                        {/* TIER 1: TOP STICKY HEADER (5-Column Grid, h-7 + h-9, sticky top-0 z-50) */}
                        {/* ══════════════════════════════════════════════════════════════════ */}
                        <div className="sticky top-0 z-50 bg-slate-900 shadow-md">
                            {/* Tier 1 Column Header Row: Saved Set | Source | Flow | Target | (Empty 70px) */}
                            <div className="grid grid-cols-[250px_minmax(150px,1fr)_80px_minmax(150px,1fr)_70px] gap-2 items-center h-7 px-2 bg-slate-950 text-[10px] font-bold text-slate-400 uppercase tracking-wider border-b border-slate-800 shrink-0">
                                <div className="pl-6">Saved Set</div>
                                <div>Source</div>
                                <div className="text-center">Flow</div>
                                <div>Target</div>
                                <div className="w-[70px] shrink-0" />
                            </div>

                            {/* Tier 1 Set Control Row with Col 5 Inline Ghost Actions */}
                            <div className="relative group grid grid-cols-[250px_minmax(150px,1fr)_80px_minmax(150px,1fr)_70px] gap-2 items-center h-9 max-h-9 px-2 py-0.5 border-b border-slate-750 bg-slate-900 text-xs">
                                {/* Col 1 (250px): Set Selector Dropdown + Default Star */}
                                <div className="flex items-center gap-1.5 min-w-0">
                                    <span className="material-symbols-outlined text-amber-400 text-[18px] shrink-0">bookmark</span>
                                    <select
                                        value={selectedSetName}
                                        onChange={e => {
                                            setSelectedSetName(e.target.value);
                                            setExpandedPresetId(null);
                                        }}
                                        className="w-full bg-slate-950 border border-slate-700 hover:border-slate-600 text-white font-bold text-xs rounded px-2 h-7 focus:outline-none focus:border-amber-500 cursor-pointer truncate"
                                        title="Select Mapping Set"
                                    >
                                        {standardSets.map(s => {
                                            const sName = s.setName || s.name || s;
                                            return (
                                                <option key={sName} value={sName}>
                                                    {sName}{s.isDefault ? ' ★' : ''}
                                                </option>
                                            );
                                        })}
                                    </select>
                                    {isCurrentSetDefault && (
                                        <span className="px-1 py-0.2 rounded text-[10px] font-bold bg-amber-950/80 text-amber-300 border border-amber-800/80 shrink-0" title="Active default set">
                                            ★
                                        </span>
                                    )}
                                </div>

                                {/* Col 2 (1fr): Source System Selector */}
                                <div className="min-w-0">
                                    <select
                                        value={stdSourceSystem}
                                        onChange={e => setStdSourceSystem(e.target.value)}
                                        className="w-full bg-slate-950 border border-slate-700 hover:border-slate-600 text-slate-200 text-xs rounded px-2 h-7 focus:outline-none focus:border-indigo-500 cursor-pointer truncate"
                                    >
                                        {stdAvailableSources.map(s => (
                                            <option key={s} value={s}>{s}</option>
                                        ))}
                                    </select>
                                </div>

                                {/* Col 3 (80px, center): Directional Flow ➔ */}
                                <div className="flex items-center justify-center gap-1 text-center select-none text-slate-500 font-bold text-xs">
                                    ➔
                                </div>

                                {/* Col 4 (1fr): Target System Selector */}
                                <div className="min-w-0">
                                    <select
                                        value={stdTargetSystem}
                                        onChange={e => setStdTargetSystem(e.target.value)}
                                        className="w-full bg-slate-950 border border-slate-700 hover:border-slate-600 text-slate-200 text-xs rounded px-2 h-7 focus:outline-none focus:border-indigo-500 cursor-pointer truncate"
                                    >
                                        {stdAvailableTargets.map(t => (
                                            <option key={t} value={t}>{t}</option>
                                        ))}
                                    </select>
                                </div>

                                {/* Col 5 (70px): Inline Ghost Actions (Set Actions popover + Close button) */}
                                <div className="opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity flex gap-1.5 items-center justify-end pr-1 relative">
                                    {/* Auto-save status mini indicator */}
                                    {isAutoSaving && (
                                        <span className="material-symbols-outlined text-indigo-400 text-[14px] animate-spin shrink-0" title="Saving changes...">sync</span>
                                    )}

                                    {/* Set Actions Popover Menu Trigger */}
                                    <div className="relative">
                                        <button
                                            type="button"
                                            onClick={() => setShowSetActionsMenu(prev => !prev)}
                                            className="h-6 w-6 flex items-center justify-center text-slate-400 hover:text-white hover:bg-slate-800 rounded transition-colors focus:outline-none"
                                            title="Set Actions"
                                        >
                                            <span className="material-symbols-outlined text-[15px]">settings</span>
                                        </button>

                                        {showSetActionsMenu && (
                                            <div
                                                ref={setActionsMenuRef}
                                                className="absolute right-0 top-full mt-1 w-44 bg-slate-950 border border-slate-700 rounded-lg shadow-2xl p-1 z-50 text-xs space-y-0.5 animate-in fade-in zoom-in-95 duration-100"
                                            >
                                                <button
                                                    type="button"
                                                    onClick={() => { setShowSetActionsMenu(false); setShowNewSetModal(true); }}
                                                    className="w-full text-left px-2 py-1.5 hover:bg-slate-850 rounded text-amber-300 flex items-center gap-1.5"
                                                >
                                                    <span className="material-symbols-outlined text-[14px]">add</span>
                                                    New Set
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => { setShowSetActionsMenu(false); setRenamingSetName(selectedSetName); setShowRenameSetModal(true); }}
                                                    className="w-full text-left px-2 py-1.5 hover:bg-slate-850 rounded text-slate-300 hover:text-white flex items-center gap-1.5"
                                                >
                                                    <span className="material-symbols-outlined text-[14px]">edit</span>
                                                    Rename Set
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => { setShowSetActionsMenu(false); handleDuplicateSet(selectedSetName); }}
                                                    className="w-full text-left px-2 py-1.5 hover:bg-slate-850 rounded text-slate-300 hover:text-white flex items-center gap-1.5"
                                                >
                                                    <span className="material-symbols-outlined text-[14px]">content_copy</span>
                                                    Duplicate Set
                                                </button>
                                                {!isCurrentSetDefault && (
                                                    <button
                                                        type="button"
                                                        onClick={() => { setShowSetActionsMenu(false); handleMakeDefaultSet(selectedSetName); }}
                                                        className="w-full text-left px-2 py-1.5 hover:bg-slate-850 rounded text-amber-300 flex items-center gap-1.5"
                                                    >
                                                        <span className="material-symbols-outlined text-[14px]">star</span>
                                                        Make Default
                                                    </button>
                                                )}
                                                <button
                                                    type="button"
                                                    onClick={() => { setShowSetActionsMenu(false); handleResetSetDefaults(selectedSetName); }}
                                                    className="w-full text-left px-2 py-1.5 hover:bg-slate-850 rounded text-slate-300 hover:text-white flex items-center gap-1.5"
                                                >
                                                    <span className="material-symbols-outlined text-[14px]">restart_alt</span>
                                                    Reset Defaults
                                                </button>
                                                <button
                                                    type="button"
                                                    disabled={isCurrentSetDefault || standardSets.length <= 1}
                                                    onClick={() => { setShowSetActionsMenu(false); handleDeleteSet(selectedSetName); }}
                                                    className="w-full text-left px-2 py-1.5 hover:bg-rose-950/60 rounded text-rose-400 disabled:opacity-40 disabled:hover:bg-transparent flex items-center gap-1.5"
                                                >
                                                    <span className="material-symbols-outlined text-[14px]">delete</span>
                                                    Delete Set
                                                </button>
                                            </div>
                                        )}
                                    </div>

                                    {/* Close Button */}
                                    {onClose && (
                                        <button
                                            type="button"
                                            onClick={handleClose}
                                            className="h-6 w-6 flex items-center justify-center text-slate-400 hover:text-rose-400 hover:bg-slate-850 rounded transition-colors focus:outline-none shrink-0"
                                            title="Close (Esc)"
                                        >
                                            <span className="material-symbols-outlined text-[16px]">close</span>
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* ══════════════════════════════════════════════════════════════════ */}
                        {/* TIER 2: PIPELINE COLUMN HEADER (5-Column Grid, h-7, sticky top-[64px] z-45) */}
                        {/* ══════════════════════════════════════════════════════════════════ */}
                        <div className="grid grid-cols-[250px_minmax(150px,1fr)_80px_minmax(150px,1fr)_70px] gap-2 items-center h-7 px-2 bg-slate-950 text-[10px] font-bold text-slate-400 uppercase tracking-wider border-b border-slate-800 sticky top-[64px] z-45 shrink-0 shadow-sm">
                            {/* Col 1 (250px): Pipeline with Ghost Search Filter */}
                            <div className="pl-6 group flex items-center justify-between pr-2 min-w-0">
                                <span className="truncate">Pipeline</span>
                                {isPipelineSearchExpanded || pipelineFilter ? (
                                    <div className="flex items-center gap-1">
                                        <span className="material-symbols-outlined text-[13px] text-slate-400">search</span>
                                        <input
                                            type="text"
                                            autoFocus
                                            value={pipelineFilter}
                                            onChange={e => setPipelineFilter(e.target.value)}
                                            onBlur={() => {
                                                if (!pipelineFilter.trim()) {
                                                    setIsPipelineSearchExpanded(false);
                                                }
                                            }}
                                            onKeyDown={e => {
                                                if (e.key === 'Escape') {
                                                    setPipelineFilter('');
                                                    setIsPipelineSearchExpanded(false);
                                                }
                                            }}
                                            placeholder="Search pipelines..."
                                            className="bg-slate-950 border border-slate-700 rounded px-1.5 py-0 h-5 text-[10px] text-white placeholder-slate-500 focus:border-indigo-500 focus:outline-none w-28 normal-case font-normal"
                                        />
                                        {pipelineFilter && (
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setPipelineFilter('');
                                                    setIsPipelineSearchExpanded(false);
                                                }}
                                                className="text-slate-400 hover:text-white text-xs font-bold"
                                            >&times;</button>
                                        )}
                                    </div>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={() => setIsPipelineSearchExpanded(true)}
                                        className="opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity p-0.5 rounded hover:bg-slate-800 text-slate-400 hover:text-white"
                                        title="Search pipelines (Click to expand)"
                                    >
                                        <span className="material-symbols-outlined text-[14px]">search</span>
                                    </button>
                                )}
                            </div>

                            {/* Col 2 (1fr): Source Table */}
                            <div>Source Table</div>

                            {/* Col 3 (80px): Flow */}
                            <div className="text-center">Flow</div>

                            {/* Col 4 (1fr): Target Table */}
                            <div>Target Table</div>

                            {/* Col 5 (70px): Empty */}
                            <div className="w-[70px] shrink-0" />
                        </div>


                        {/* Loading state */}
                        {loadingStandard ? (
                            <div className="flex items-center justify-center py-20 text-slate-400 text-xs">
                                <span className="material-symbols-outlined animate-spin text-2xl mr-2 text-amber-400">sync</span>
                                Loading standard mapping catalog...
                            </div>
                        ) : (
                            <>
                                {/* Pipeline List */}
                                {filteredPresets.length === 0 ? (
                                    <div className="py-8 text-center text-slate-500 text-xs">
                                        No pipelines found in set "{selectedSetName}". Use the ghost row below to create one.
                                    </div>
                                ) : (
                                    filteredPresets.map(preset => {
                                        const presetId = preset.MappingID || preset.TargetQBEntity || preset.EntityType;
                                        const isPresetExpanded = expandedPresetId === presetId;

                                        return (
                                            <PresetAccordionItem
                                                key={presetId}
                                                preset={preset}
                                                isExpanded={isPresetExpanded}
                                                onToggleExpand={() => {
                                                    // Strict single active pipeline: expanding B collapses A
                                                    setExpandedPresetId(prev => prev === presetId ? null : presetId);
                                                }}
                                                token={token}
                                                sourceSystem={stdSourceSystem}
                                                targetSystem={stdTargetSystem}
                                                availableTables={availableTables}
                                                targetEntities={stdTargetEntities}
                                                assignedEntitiesInSet={assignedEntitiesInSet}
                                                onSavePreset={handleSavePreset}
                                                onDuplicatePreset={handleDuplicatePreset}
                                                onRenamePreset={handleInlineRenamePipeline}
                                                onDeletePreset={handleDeletePreset}
                                                onResetPresetDefaults={handleResetPresetDefaults}
                                                onSavingStateChange={setIsAutoSaving}
                                            />
                                        );
                                    })
                                )}

                                {/* Persistent Tier 2 Pipeline Ghost Row */}
                                <div className="grid grid-cols-[250px_minmax(150px,1fr)_80px_minmax(150px,1fr)_70px] gap-2 items-center h-9 max-h-9 px-2 py-0.5 border-b border-dashed border-slate-700/60 bg-slate-900/40 hover:bg-slate-850/40 transition-colors">
                                    {/* Col 1 (250px): Pipeline Name Input */}
                                    <div className="pl-6 min-w-0">
                                        <input
                                            type="text"
                                            value={ghostPipelineName}
                                            onChange={e => {
                                                setGhostPipelineName(e.target.value);
                                                setGhostPipelineNameUserEdited(true);
                                            }}
                                            placeholder="+ New pipeline name..."
                                            className="w-full bg-slate-950/80 border border-slate-750 hover:border-slate-600 rounded px-2 py-0 h-7 text-xs text-white placeholder-slate-500 focus:border-indigo-500 focus:outline-none"
                                        />
                                    </div>

                                    {/* Col 2 (1fr): ERP Source Table */}
                                    <div className="min-w-0">
                                        <SearchableSelect
                                            options={ghostTableOptions}
                                            value={ghostSourceTable}
                                            onChange={val => setGhostSourceTable(val || '')}
                                            placeholder="Optional source table..."
                                            allowCustom={true}
                                            className="w-full text-xs"
                                        />
                                    </div>

                                    {/* Col 3 (80px, center): Flow ➔ */}
                                    <div className="flex items-center justify-center text-center select-none text-slate-500 font-bold text-xs">
                                        ➔
                                    </div>

                                    {/* Col 4 (1fr): Target Table Dropdown (Strictly unassigned tables) */}
                                    <div className="min-w-0">
                                        <SearchableSelect
                                            options={ghostEntityOptions}
                                            value=""
                                            onChange={handleGhostEntitySelect}
                                            placeholder="+ Select target table..."
                                            className="w-full text-xs"
                                        />
                                    </div>

                                    {/* Col 5 (70px): Ghost Column Placeholder */}
                                    <div className="w-[70px] shrink-0" />
                                </div>
                            </>
                        )}
                    </div>
                </div>
            ) : (
                /* ── PIPELINE MODE: ETL CANVAS / DIFF / SYNC ── */
                <div className="flex-1 overflow-hidden relative flex flex-col">
                    {/* MODAL HEADER FOR PIPELINE MODE */}
                    <div className="flex flex-col border-b border-slate-750 bg-slate-850 shrink-0 shadow-md z-30">
                        <div className="flex justify-between items-center px-6 py-3.5 border-b border-slate-800 bg-slate-850">
                            <div className="flex items-center gap-3">
                                <div className="flex items-center gap-2.5">
                                    <span className="material-symbols-outlined text-indigo-400 text-xl">sync_alt</span>
                                    <div>
                                        <h2 className="text-sm font-bold text-white tracking-wide">
                                            {config?.ConfigName || 'Pipeline Field Mapping Designer'}
                                        </h2>
                                        <div className="text-[11px] text-slate-400">
                                            {config?.ConfigID ? `Pipeline #${config.ConfigID}` : 'Interactive ETL Designer'} &bull; {effectiveSourceSystem} ➔ {config?.TargetSystem || 'QuickBooks'}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="flex items-center gap-3 shrink-0">
                                <div className="flex space-x-1 bg-slate-900 p-0.5 rounded-lg border border-slate-750 mr-2">
                                    <button
                                        type="button"
                                        onClick={() => setPipelineTab('mappings')}
                                        className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                                            pipelineTab === 'mappings' ? 'bg-indigo-600 text-white shadow-sm' : 'text-slate-400 hover:text-slate-200'
                                        }`}
                                    >
                                        Mappings
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setPipelineTab('diff')}
                                        className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                                            pipelineTab === 'diff' ? 'bg-indigo-600 text-white shadow-sm' : 'text-slate-400 hover:text-slate-200'
                                        }`}
                                    >
                                        Inspect & Diff
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setPipelineTab('sync')}
                                        className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                                            pipelineTab === 'sync' ? 'bg-emerald-600 text-white shadow-sm' : 'text-slate-400 hover:text-slate-200'
                                        }`}
                                    >
                                        Sync Execution
                                    </button>
                                </div>

                                {onResetDefaults && (
                                    <button
                                        type="button"
                                        onClick={onResetDefaults}
                                        disabled={saving}
                                        className="px-3 py-1.5 bg-slate-800 hover:bg-slate-750 text-slate-300 hover:text-white border border-slate-750 text-xs font-semibold rounded-lg transition-colors shadow-sm"
                                    >
                                        Reset Defaults
                                    </button>
                                )}

                                <button
                                    type="button"
                                    onClick={handleSavePipeline}
                                    disabled={saving}
                                    className={`px-4 py-1.5 text-xs font-semibold rounded-lg shadow transition-colors flex items-center gap-1.5 ${
                                        isDirty ? 'bg-emerald-600 hover:bg-emerald-500 text-white' : 'bg-slate-800 text-slate-300 border border-slate-700 hover:text-white'
                                    } disabled:opacity-50`}
                                >
                                    {saving ? (
                                        <>
                                            <span className="material-symbols-outlined text-[14px] animate-spin">sync</span>
                                            <span>Saving...</span>
                                        </>
                                    ) : (
                                        <>
                                            <span className="material-symbols-outlined text-[15px]">save</span>
                                            <span>Save Mappings</span>
                                        </>
                                    )}
                                </button>

                                {onClose && (
                                    <button
                                        type="button"
                                        onClick={handleClose}
                                        className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-750 rounded-lg transition-colors ml-1 focus:outline-none"
                                        title="Close Modal (Esc)"
                                    >
                                        <span className="material-symbols-outlined text-[20px]">close</span>
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Pipeline Mode Scope Sub-Header */}
                        <div className="px-6 py-3 bg-slate-900/95 space-y-2.5">
                            <div className="grid grid-cols-[250px_minmax(0,1fr)_80px_minmax(0,1fr)_100px] gap-3 items-center">
                                <div className="text-xs font-bold text-slate-300 flex items-center gap-1.5">
                                    <span className="material-symbols-outlined text-[16px] text-sky-400">table_chart</span>
                                    Pipeline Target:
                                </div>

                                <div>
                                    <SearchableSelect
                                        options={tableSelectOptions}
                                        value={selectedTable || activeTableName}
                                        onChange={val => {
                                            setSelectedTable((val || '').toUpperCase());
                                            setIsDirty(true);
                                        }}
                                        placeholder="Search ERP source table..."
                                        allowCustom={true}
                                        className="w-full text-xs"
                                    />
                                </div>

                                <div className="flex items-center justify-center gap-1 text-center select-none">
                                    <span className="text-slate-500 font-bold text-sm">➔</span>
                                    <span className="px-2 py-0.5 rounded-full bg-slate-950 border border-slate-700 text-sky-300 font-mono text-[10px]">
                                        {activeMappedCount} Mapped
                                    </span>
                                </div>

                                <div>
                                    <select
                                        value={selectedQBEntity}
                                        onChange={e => {
                                            const canonical = normalizeEntityName(e.target.value);
                                            setSelectedQBEntity(canonical);
                                            setSelectedEntity(canonical);
                                            setIsDirty(true);
                                        }}
                                        className="bg-slate-950 border border-slate-700 hover:border-slate-600 rounded-lg px-3 py-1.5 text-xs text-purple-300 font-bold focus:border-purple-500 focus:outline-none w-full cursor-pointer"
                                    >
                                        {QUICKBOOKS_TARGET_ENTITIES.map(ent => (
                                            <option key={ent.entity} value={ent.entity}>{ent.label}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="text-right text-[11px] text-slate-500 font-mono">
                                    Live Pipeline
                                </div>
                            </div>

                            <div className="pt-1">
                                <div className="relative w-full">
                                    <span className="material-symbols-outlined text-slate-400 absolute left-3 top-2 text-[17px]">search</span>
                                    <input
                                        type="text"
                                        value={searchFilter}
                                        onChange={e => setSearchFilter(e.target.value)}
                                        placeholder="Filter source columns and target fields..."
                                        className="w-full bg-slate-950 border border-slate-700 rounded-lg pl-9 pr-8 py-1.5 text-xs text-white placeholder-slate-500 focus:border-indigo-500 focus:outline-none shadow-inner"
                                    />
                                    {searchFilter && (
                                        <button
                                            type="button"
                                            onClick={() => setSearchFilter('')}
                                            className="absolute right-2.5 top-1.5 text-slate-400 hover:text-white text-xs font-bold"
                                            title="Clear filter"
                                        >&times;</button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>


                    {pipelineTab === 'mappings' && (
                        <MappingDesignerTable
                            sourceColumns={sourceColumns}
                            isStandardMode={false}
                            selectedQBEntity={selectedQBEntity}
                            qbSampleRecords={qbSampleRecords}
                            activeQbSampleIndex={activeQbSampleIndex}
                            setActiveQbSampleIndex={setActiveQbSampleIndex}
                            pbsSampleRecords={sampleRecords}
                            activeSampleIndex={activeSampleIndex}
                            setActiveSampleIndex={setActiveSampleIndex}
                            activeTableName={activeTableName}
                            mappings={mappings}
                            definitions={definitions}
                            onAddMapping={handleAddMappingPipeline}
                            handleCustomMappingChange={handleCustomMappingChangePipeline}
                            handleRemoveCustomRow={handleRemoveCustomRowPipeline}
                            handleResetMappingRow={handleResetMappingRowPipeline}
                        />
                    )}

                    {pipelineTab === 'diff' && (
                        <DiffAnalyzerView
                            loadingPreview={loadingDiff}
                            diffPreviewData={diffPreviewData}
                            diffDetailTab={diffDetailTab}
                            setDiffDetailTab={setDiffDetailTab}
                            selectedQBEntity={selectedQBEntity}
                            handleGenerateDiff={handleRunDiff}
                        />
                    )}

                    {pipelineTab === 'sync' && (
                        <SyncRunnerView
                            selectedQBEntity={selectedQBEntity}
                            syncLimit={syncLimit}
                            setSyncLimit={setSyncLimit}
                            loadingSync={loadingSync}
                            syncResult={syncResult}
                            handleExecuteLiveSync={handleExecuteLiveSync}
                        />
                    )}
                </div>
            )}

            {/* ══════════════════════════════════════════════════════════════════ */}
            {/* MODAL DIALOGS (Set & Pipeline Management)                           */}
            {/* ══════════════════════════════════════════════════════════════════ */}
            <NewSetModal
                isOpen={showNewSetModal}
                onClose={() => setShowNewSetModal(false)}
                availableSets={standardSets.map(s => s.setName || s.name || s)}
                onSubmit={handleCreateSetSubmit}
            />

            <RenameSetModal
                isOpen={showRenameSetModal}
                onClose={() => {
                    setShowRenameSetModal(false);
                    setRenamingSetName(null);
                }}
                currentSetName={renamingSetName}
                onSubmit={handleRenameSetSubmit}
            />

            <NewPipelineModal
                isOpen={showNewPipelineModal}
                onClose={() => {
                    setShowNewPipelineModal(false);
                    setAddingToSetName(null);
                }}
                setName={addingToSetName}
                sourceSystem={stdSourceSystem}
                targetSystem={stdTargetSystem}
                targetEntities={stdTargetEntities}
                availableTables={availableTables}
                onSubmit={({ targetEntity, sourceTable, pipelineName }) => {
                    handleAddNewPipelineSubmit({
                        setName: addingToSetName,
                        targetEntity,
                        sourceTable,
                        pipelineName
                    });
                }}
            />

            <RenamePipelineModal
                isOpen={showRenamePipelineModal}
                onClose={() => {
                    setShowRenamePipelineModal(false);
                    setRenamingPreset(null);
                }}
                currentName={renamingPreset?.MappingName}
                onSubmit={handleRenamePipelineSubmit}
            />

            {/* Floating Toast Notification */}
            {(saveMessage || notification) && (() => {
                const activeToast = saveMessage || notification;
                return (
                    <div
                        role="status"
                        aria-live="polite"
                        className={`fixed bottom-6 right-6 z-[100] flex items-center gap-2.5 px-4 py-2.5 rounded-lg shadow-2xl border text-xs font-medium backdrop-blur-md transition-all duration-300 animate-in fade-in slide-in-from-bottom-3 ${
                            activeToast.type === 'success'
                                ? 'bg-emerald-950/95 border-emerald-600/80 text-emerald-200 shadow-emerald-950/60'
                                : activeToast.type === 'error'
                                    ? 'bg-rose-950/95 border-rose-600/80 text-rose-200 shadow-rose-950/60'
                                    : 'bg-blue-950/95 border-blue-600/80 text-blue-200 shadow-blue-950/60'
                        }`}
                    >
                        <span className="material-symbols-outlined text-[18px] shrink-0">
                            {activeToast.type === 'success' ? 'check_circle' : activeToast.type === 'error' ? 'error' : 'info'}
                        </span>
                        <span className="max-w-md truncate">{activeToast.text}</span>
                        <button
                            type="button"
                            onClick={() => {
                                setSaveMessage(null);
                                if (onClearNotification) onClearNotification();
                            }}
                            className="ml-2 text-slate-400 hover:text-white p-0.5 rounded hover:bg-white/10 transition-colors focus:outline-none"
                            title="Dismiss notification"
                        >
                            <span className="material-symbols-outlined text-[16px]">close</span>
                        </button>
                    </div>
                );
            })()}
        </div>
    );
};

export default FieldMappingDesigner;
